import { useEffect, useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "../../../../components/UI/Dialog";
import shopApi from "../../../../api/shopApi";
import { cn } from "../../../../utils/utils";

const removeAccents = (s) =>
  (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const groupByFirstLetter = (list) => {
  const sorted = [...list].sort((a, b) =>
    removeAccents(a.displayName)
      .toLowerCase()
      .localeCompare(removeAccents(b.displayName).toLowerCase()),
  );
  const buckets = new Map();
  for (const item of sorted) {
    const first = removeAccents(item.displayName || "").charAt(0).toUpperCase();
    const key = /[A-Z]/.test(first) ? first : "#";
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(item);
  }
  return Array.from(buckets.entries()).map(([letter, items]) => ({
    letter,
    items,
  }));
};

const Pill = ({ active, onClick, color, dashed, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "shrink-0 h-8 px-3 rounded-full border text-[13px] font-medium transition-colors whitespace-nowrap",
      active
        ? "bg-blue-500 text-white border-blue-500"
        : "bg-gray-100 text-gray-700 border-transparent hover:bg-gray-200",
      dashed && !active && "border-dashed border-gray-300 bg-white",
    )}
    style={
      active && color
        ? { backgroundColor: color, borderColor: color, color: "#fff" }
        : undefined
    }
  >
    {children}
  </button>
);

const SendContactCardModal = ({
  open,
  onOpenChange,
  shopId,
  akabizTags = [],
  onSend,
}) => {
  const [friends, setFriends] = useState([]);
  const [zaloTags, setZaloTags] = useState([]);
  const [query, setQuery] = useState("");
  const [filterKey, setFilterKey] = useState("all");
  const [selected, setSelected] = useState(() => new Set());
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setFilterKey("all");
    setSelected(new Set());
    if (!shopId) return;

    setIsLoading(true);
    Promise.all([
      shopApi.getFriendContacts(shopId).catch(() => null),
      shopApi.getTagZalos(shopId).catch(() => null),
    ])
      .then(([friendsRes, tagsRes]) => {
        setFriends(friendsRes?.data || []);
        setZaloTags(tagsRes?.data || []);
      })
      .finally(() => setIsLoading(false));
  }, [open, shopId]);

  const filtered = useMemo(() => {
    const q = removeAccents(query.trim()).toLowerCase();
    return friends.filter((c) => {
      if (q) {
        const name = removeAccents(c.displayName || "").toLowerCase();
        if (!name.includes(q)) return false;
      }
      if (filterKey === "all") return true;
      if (filterKey.startsWith("zalo:")) {
        const tagId = filterKey.slice(5);
        const tag = zaloTags.find(
          (t) => String(t.tagId) === tagId || String(t.id) === tagId,
        );
        return tag?.conversations?.includes(c.uid) || false;
      }
      if (filterKey.startsWith("akabiz:")) {
        const tagId = filterKey.slice(7);
        return (c.tagIds || []).includes(tagId);
      }
      return true;
    });
  }, [friends, query, filterKey, zaloTags]);

  const grouped = useMemo(() => groupByFirstLetter(filtered), [filtered]);

  const toggleOne = (uid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const handleSend = () => {
    const picked = friends.filter((c) => selected.has(c.uid));
    if (picked.length === 0) return;
    onSend?.(picked);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] p-0 gap-0 overflow-hidden">
        <DialogTitle className="sr-only">Gửi danh thiếp</DialogTitle>
        <DialogDescription className="sr-only">
          Chọn một hoặc nhiều bạn bè để gửi danh thiếp
        </DialogDescription>
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-medium text-base">Gửi danh thiếp</h3>
          <button
            onClick={() => onOpenChange(false)}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Đóng"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              className="w-full h-10 rounded-full border border-gray-300 pl-9 pr-4 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="Tìm danh thiếp theo tên"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="px-5 pb-2 flex gap-1.5 overflow-x-auto no-scrollbar">
          <Pill
            active={filterKey === "all"}
            onClick={() => setFilterKey("all")}
          >
            Tất cả
          </Pill>
          {zaloTags.map((t) => {
            const id = String(t.tagId ?? t.id);
            return (
              <Pill
                key={`zalo-${id}`}
                active={filterKey === `zalo:${id}`}
                onClick={() => setFilterKey(`zalo:${id}`)}
                color={t.color}
              >
                {t.text || t.name}
              </Pill>
            );
          })}
          {akabizTags.map((t) => {
            const id = String(t._id || t.id);
            return (
              <Pill
                key={`akabiz-${id}`}
                active={filterKey === `akabiz:${id}`}
                onClick={() => setFilterKey(`akabiz:${id}`)}
                color={t.color}
                dashed
              >
                {t.name}
              </Pill>
            );
          })}
        </div>

        <div className="h-[420px] overflow-y-auto">
          {isLoading && (
            <div className="text-center text-gray-500 py-8 text-sm">
              Đang tải...
            </div>
          )}
          {!isLoading && grouped.length === 0 && (
            <div className="text-center text-gray-500 py-8 text-sm">
              Không tìm thấy danh thiếp
            </div>
          )}
          {!isLoading &&
            grouped.map(({ letter, items }) => (
              <div key={letter}>
                <div className="sticky top-0 bg-white px-5 py-1 text-sm font-semibold text-gray-700 z-10">
                  {letter}
                </div>
                {items.map((c) => {
                  const checked = selected.has(c.uid);
                  return (
                    <label
                      key={c.uid}
                      className="flex items-center gap-3 px-5 py-2 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleOne(c.uid)}
                        className="w-4 h-4 accent-blue-500"
                      />
                      {c.avatar ? (
                        <img
                          src={c.avatar}
                          alt=""
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-sm">
                          {(c.displayName || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span className="text-sm text-gray-900 flex-1 truncate">
                        {c.displayName}
                      </span>
                    </label>
                  );
                })}
              </div>
            ))}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t bg-white">
          <button
            onClick={() => onOpenChange(false)}
            className="px-4 h-9 rounded-md bg-gray-100 text-gray-700 text-sm hover:bg-gray-200"
          >
            Hủy
          </button>
          <button
            disabled={selected.size === 0}
            onClick={handleSend}
            className="px-4 h-9 rounded-md bg-blue-500 text-white text-sm disabled:bg-blue-200 disabled:cursor-not-allowed hover:bg-blue-600"
          >
            Gửi danh thiếp{selected.size > 1 ? ` (${selected.size})` : ""}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SendContactCardModal;
